import { useState } from "react";
import i18n from "../../../language/i18n";
import { Icon, ScoreBar } from "../../../library";
import { PrerequisiteWorkGroup } from "../../../utilization/PrerequisiteWorkUtilization";
import { prerequisiteWorkSectionStyles } from "./PrerequisiteWorkSectionStyles";

// "Ön koşul çalışmaları" — the readiness (hazır bulunuşluk) practice the learner has COMPLETED, one
// row per ön koşul konu, expandable into the individual çalışma.
//
// Only solved work reaches this component (see buildPrerequisiteWorkGroups). Listing the ön koşullar
// the learner has NOT done would turn a record of effort into a backlog, on the one screen whose job
// is to show what they have achieved — the learning path is where remaining work belongs. A learner
// with no completed readiness work renders nothing at all: there is no empty state, because having
// none is the ordinary case rather than a gap needing explanation.

interface PrerequisiteWorkSectionProps {
  groups: PrerequisiteWorkGroup[];
  /** The learner's UI language, for the dates (the context's persisted "TR"/"EN"). */
  language: string;
}

/** Scores are 0–1 on the wire and a percentage on screen. A null score has no percentage at all. */
const toPercentage = (score: number | null): number | null =>
  score == null ? null : score * 100;

const formatMinutes = (timeSpentMs: number): number =>
  Math.max(0, Math.round(timeSpentMs / 1000 / 60));

// NOTE: the key really is `minute_abbrevation` — the repo-wide spelling carries a typo (one "i")
// and five other call sites use it. Spelling it correctly here would silently render the raw key,
// since i18n.ts sets no fallbackLng and no parseMissingKeyHandler. Renaming it everywhere is a
// separate sweep; matching the existing key is what keeps this screen readable today.

const PrerequisiteWorkSection = ({
  groups,
  language,
}: PrerequisiteWorkSectionProps) => {
  const [openCategoryIdSet, setOpenCategoryIdSet] = useState<Set<number>>(
    new Set(),
  );

  if (!groups || groups.length === 0) return null;

  const toggle = (categoryId: number) => {
    setOpenCategoryIdSet((previous) => {
      const next = new Set(previous);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const formatDate = (completedAt: number | null): string =>
    completedAt == null
      ? "—"
      : new Date(completedAt).toLocaleDateString(language, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

  const renderScore = (score: number | null, size: "small" | "medium") => {
    const percentage = toPercentage(score);
    // A konu whose every question was skipped has no score to show. Rendering 0% would report a
    // failure that never happened — the same rule the gelişim chart applies to its gaps.
    if (percentage == null) return <span className="pwk-score-empty">—</span>;
    return <ScoreBar score={percentage} size={size} />;
  };

  return (
    <div className="pwk-card">
      <style>{prerequisiteWorkSectionStyles}</style>
      <div className="pwk-head">
        <p className="pwk-title">{i18n.t("prerequisite_work_section_title")}</p>
        <p className="pwk-subtitle">{i18n.t("prerequisite_work_section_hint")}</p>
      </div>
      <div className="pwk-list">
        {groups.map((group) => {
          const isOpen = openCategoryIdSet.has(group.categoryId);
          const metaParts = [
            i18n.t("prerequisite_attempt_count", { count: group.attemptCount }),
            i18n.t("prerequisite_question_progress", {
              answered: group.answeredCount,
              total: group.questionCount,
            }),
            `${formatMinutes(group.totalTimeSpentMs)} ${i18n.t("minute_abbrevation")}`,
            `${i18n.t("prerequisite_last_worked")}: ${formatDate(group.lastWorkedAt)}`,
          ];
          return (
            <div
              className={`pwk-row ${isOpen ? "is-open" : ""}`}
              key={group.categoryId}
            >
              <button
                type="button"
                className="pwk-row-head"
                onClick={() => toggle(group.categoryId)}
                aria-expanded={isOpen}
              >
                <span className="pwk-chevron">
                  <Icon name="chevronRight" size={16} />
                </span>
                <span className="pwk-row-main">
                  <span className="pwk-topic">
                    <span
                      className={`pwk-topic-name ${
                        group.categoryName ? "" : "is-unknown"
                      }`}
                    >
                      {group.categoryName || i18n.t("prerequisite_unknown_topic")}
                    </span>
                    {group.gradeLevel != null ? (
                      <span className="pwk-grade-chip">
                        {`${group.gradeLevel}. ${i18n.t("grade")}`}
                      </span>
                    ) : null}
                  </span>
                  <span className="pwk-meta">{metaParts.join(" · ")}</span>
                </span>
                <span className="pwk-score">
                  {renderScore(group.avgScore, "medium")}
                </span>
              </button>
              {isOpen ? (
                <div className="pwk-detail">
                  {group.assignmentRows.map((row, index) => (
                    <div
                      className="pwk-detail-row"
                      key={row.personalizedAssignmentId ?? `row-${index}`}
                    >
                      <span className="pwk-detail-main">
                        <span className="pwk-detail-date">
                          {formatDate(row.completedAt)}
                        </span>
                        <span className="pwk-detail-meta">
                          {[
                            i18n.t("prerequisite_question_progress", {
                              answered: row.answeredCount,
                              total: row.questionCount,
                            }),
                            `${formatMinutes(row.timeSpentMs)} ${i18n.t("minute_abbrevation")}`,
                          ].join(" · ")}
                        </span>
                      </span>
                      <span className="pwk-detail-score">
                        {renderScore(row.score, "small")}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PrerequisiteWorkSection;
